import { ErrorIcon, EyeHiddenIcon, EyeIcon } from '@/components/icons'
import {
  FormHelperText,
  IconButton,
  InputBase,
  Stack,
  inputBaseClasses,
} from '@mui/material'
import { formHelperTextClasses } from '@mui/material/FormHelperText'
import React, { Fragment, useCallback, useRef, useState } from 'react'
import { Controller, UseFormReturn } from 'react-hook-form'

interface PinInputProps {
  name: string
  form: UseFormReturn<any, any>
  length?: number
  showErrorMessage?: boolean
  defaultMasked?: boolean
}

const PIN_LENGTH = 6

const PinInput = ({
  name,
  form,
  length = PIN_LENGTH,
  showErrorMessage = true,
  defaultMasked = true,
}: PinInputProps) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(''))
  const [isMasked, setIsMasked] = useState(defaultMasked)
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  const {
    control,
    watch,
    formState: { errors },
  } = form

  const fieldValue = watch(name)

  const focusInput = useCallback(
    (index: number) => {
      if (index >= 0 && index < length) {
        inputRefs.current[index]?.focus()
      }
    },
    [length],
  )

  React.useEffect(() => {
    const val = fieldValue ?? ''
    const currentJoined = values.join('')
    if (val !== currentJoined) {
      const digits = val.slice(0, length).split('')
      const newValues = Array(length).fill('')
      digits.forEach((digit: string, i: number) => {
        newValues[i] = digit
      })
      setValues(newValues)
      if (!val) {
        focusInput(0)
      }
    }
  }, [fieldValue, length, values, focusInput])

  const updateValues = useCallback(
    (newValues: string[], onChange: (value: string) => void) => {
      setValues(newValues)
      onChange(newValues.join(''))
    },
    [],
  )

  const handleChange = useCallback(
    (
      index: number,
      e: React.ChangeEvent<HTMLInputElement>,
      onChange: (value: string) => void,
    ) => {
      const input = e.target.value
      // Only accept single digit
      const digit = input.replace(/\D/g, '').slice(-1)
      if (!digit) return

      const newValues = [...values]
      newValues[index] = digit
      updateValues(newValues, onChange)

      // Auto-focus next input
      if (index < length - 1) {
        focusInput(index + 1)
      }
    },
    [values, length, focusInput, updateValues],
  )

  const handleKeyDown = useCallback(
    (
      index: number,
      e: React.KeyboardEvent<HTMLInputElement>,
      onChange: (value: string) => void,
    ) => {
      if (e.key === 'Backspace') {
        e.preventDefault()
        const newValues = [...values]
        if (values[index]) {
          // Clear current input
          newValues[index] = ''
          updateValues(newValues, onChange)
        } else if (index > 0) {
          // Move to previous input and clear it
          newValues[index - 1] = ''
          updateValues(newValues, onChange)
          focusInput(index - 1)
        }
      } else if (e.key === 'ArrowLeft' && index > 0) {
        focusInput(index - 1)
      } else if (e.key === 'ArrowRight' && index < length - 1) {
        focusInput(index + 1)
      }
    },
    [values, length, focusInput, updateValues],
  )

  const handlePaste = useCallback(
    (
      e: React.ClipboardEvent<HTMLInputElement>,
      onChange: (value: string) => void,
    ) => {
      e.preventDefault()
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '')
      if (!pastedData) return

      const digits = pastedData.slice(0, length).split('')
      const newValues = Array(length).fill('')
      digits.forEach((digit, i) => {
        newValues[i] = digit
      })
      updateValues(newValues, onChange)

      // Focus the next empty input or the last one
      const nextEmptyIndex = newValues.findIndex((v) => !v)
      focusInput(nextEmptyIndex >= 0 ? nextEmptyIndex : length - 1)
    },
    [length, focusInput, updateValues],
  )

  const hasError = !!errors[name]

  return (
    <Fragment>
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, onBlur } }) => (
          <Stack
            direction="row"
            spacing="8px"
            alignItems="center"
            justifyContent={'space-between'}
          >
            {Array.from({ length }).map((_, index) => (
              <InputBase
                key={index}
                inputRef={(el) => {
                  inputRefs.current[index] = el
                }}
                value={isMasked && values[index] ? '•' : values[index]}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleChange(index, e, onChange)
                }
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  handleKeyDown(index, e, onChange)
                }
                onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                  handlePaste(e, onChange)
                }
                onBlur={onBlur}
                onFocus={(e) => e.target.select()}
                inputProps={{
                  maxLength: 1,
                  inputMode: 'numeric',
                  autoComplete: 'off',
                  'aria-label': `PIN digit ${index + 1}`,
                  style: {
                    textAlign: 'center',
                    fontSize: '20px',
                    fontWeight: 600,
                    padding: 0,
                  },
                }}
                sx={{
                  width: '44px',
                  height: '48px',
                  borderRadius: '8px',
                  border: '1.5px solid',
                  borderColor: hasError ? 'error.main' : '#D7D7D7',
                  backgroundColor: hasError
                    ? 'error.main'
                    : 'background.overrideDefault',
                  color: 'text.main',
                  transition: 'border-color 0.2s ease',
                  [`&.${inputBaseClasses.focused}`]: {
                    borderColor: hasError ? 'error.main' : 'primary.main',
                    boxShadow: hasError
                      ? 'none'
                      : '0 0 0 2px rgba(0, 128, 0, 0.15)',
                  },
                  '&:hover': {
                    borderColor: hasError ? 'error.main' : 'primary.main',
                  },
                  [`& .${inputBaseClasses.input}`]: {
                    height: '100%',
                  },
                }}
              />
            ))}
            <IconButton
              aria-label={isMasked ? 'Show digits' : 'Hide digits'}
              onClick={() => setIsMasked((prev) => !prev)}
              size="small"
              sx={{
                color: 'grey.600',
                ml: '4px !important',
                padding: '4px',
                '&:hover': { color: 'primary.main' },
              }}
            >
              {isMasked ? (
                <EyeHiddenIcon fontSize="small" />
              ) : (
                <EyeIcon fontSize="small" />
              )}
            </IconButton>
          </Stack>
        )}
      />

      {showErrorMessage && errors[name] && (
        <Stack direction="row" spacing="5px" mt="3px" color="#FF3030">
          <ErrorIcon />
          <FormHelperText
            error
            sx={{
              width: '100%',
              fontSize: '12px',
              fontWeight: '400',
              lineHeight: '115%',
              marginLeft: '9px',
              flex: 'none',
              letterSpacing: '0.045em',
              [`&.${formHelperTextClasses.error}`]: {
                color: 'text.main',
                marginLeft: '9px',
                pr: '24px',
                marginTop: '1px',
              },
            }}
          >
            {errors[name]?.message as string}
          </FormHelperText>
        </Stack>
      )}
    </Fragment>
  )
}

export default PinInput
